import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { ActionDetail, ActionDocumentRow, ActionDocumentsResponse, DocumentDto, DocumentPreview, DocumentTemplateKey } from '@tww/shared';
import { checklistFolder, fmtDate } from '@tww/shared';
import { api } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Modal } from '../../ui/Modal';
import { Note, Panel, Skeleton, StatusPill } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';

const STATE_PILL: Record<ActionDocumentRow['state'], { code: string; label: string }> = {
  present: { code: 'bestaetigt', label: 'liegt vor' },
  generated: { code: 'abstimmung', label: 'erzeugt' },
  sent: { code: 'warn', label: 'versendet' },
  draft: { code: 'warn', label: 'Entwurf' },
  missing: { code: 'muted', label: 'offen' },
};

/**
 * Schlüsseldokumente der Aktion (Plan 06 Abschnitt 5.1 links): Begehungsprotokoll, Waldbesitzervertrag, Pflanzbestätigung,
 * Monitoring-Doku mit Status und Hinweis; Knöpfe Vorschau, Erzeugen, PDF, unterschriebene Fassung hochladen.
 */
export function DocumentsPanel({ a, onOpenItem, bare }: { a: ActionDetail; onOpenItem: (key: string) => void; bare?: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const write = can('documents', 'write');
  const q = useQuery({ queryKey: ['action-documents', a.id], queryFn: () => api.get<ActionDocumentsResponse>(`/actions/${a.id}/documents`) });
  const [preview, setPreview] = useState<DocumentTemplateKey | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['action-documents', a.id] });
    qc.invalidateQueries({ queryKey: ['action', a.id] });
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['folders'] });
  };
  const generate = useMutation({
    mutationFn: (key: DocumentTemplateKey) => api.post<{ document: DocumentDto; applied: string[] }>(`/actions/${a.id}/documents/generate`, { template_key: key }),
    onSuccess: (r, key) => {
      toast(key === 'contract_owner' ? `Word-Datei erzeugt${r.applied.length ? ' und ' + r.applied[0]!.replace(/^Am/, 'am') : ''}` : `PDF erzeugt · ${r.applied.join(' · ')}`);
      invalidate();
      setPreview(null);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  /** Unterschriebene Fassung: Upload am Punkt „Projektvereinbarung Waldbesitzer“, Art Vertrag, Kennzeichen unterschrieben → „Liegt unterschrieben vor“ Ja */
  const uploadSigned = useMutation({
    mutationFn: async (file: File) => {
      const item = a.checklist.find((i) => i.template_item_key === 'vertrag');
      const fd = new FormData();
      fd.append('entity_type', 'action');
      fd.append('entity_id', String(a.id));
      fd.append('kind', 'vertrag');
      if (item) fd.append('folder', checklistFolder(item.title));
      fd.append('title', 'Waldbesitzervertrag unterschrieben');
      fd.append('note', 'unterschrieben');
      fd.append('file', file);
      const doc = await api.upload<DocumentDto>('/documents', fd);
      if (item) {
        const cur = Array.isArray(item.values.datei) ? (item.values.datei as number[]) : [];
        await api.patch(`/actions/${a.id}/checklist/${item.id}`, { values: { ...item.values, datei: [...cur, doc.id], ja: 'Ja' } });
      }
      return doc;
    },
    onSuccess: () => {
      toast('Unterschriebene Fassung abgelegt, Projektvereinbarung liegt vor');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const rows = q.data?.rows ?? [];
  const Wrap = bare ? ({ children }: { children: React.ReactNode }) => <div className="docs-bare">{children}</div> : ({ children }: { children: React.ReactNode }) => <Panel title="Dokumente aus Vorlagen">{children}</Panel>;
  return (
    <Wrap>
      {q.isLoading ? (
        <Skeleton rows={4} />
      ) : (
        <ul className="docs" style={{ padding: bare ? 0 : '0 20px' }}>
          {rows.map((r) => {
            const pill = STATE_PILL[r.state];
            return (
              <li key={r.template_key} className="docrow">
                <span className="nm">
                  <span className="ttl">
                    {r.name} <StatusPill code={pill.code} label={pill.label} />
                  </span>
                  <span>{r.hint}</span>
                  {r.document && (
                    <a className="link file" href={`/api/v1/documents/${r.document.id}/file`} target="_blank" rel="noreferrer">
                      {r.document.original_name}
                    </a>
                  )}
                </span>
                <span className="ext">
                  {r.template_key === 'site_visit' && a.site_visit && (
                    <Link className="btn sm" to={`/protokoll/${a.site_visit.id}`}>
                      Öffnen
                    </Link>
                  )}
                  <button type="button" className="btn sm" onClick={() => setPreview(r.template_key)}>
                    Vorschau
                  </button>
                  {write && r.can_generate && (
                    <button type="button" className="btn sm" onClick={() => generate.mutate(r.template_key)} disabled={generate.isPending}>
                      Erzeugen
                    </button>
                  )}
                  {write && r.template_key === 'contract_owner' && r.state !== 'present' && (
                    <button type="button" className="btn sm" onClick={() => fileInput.current?.click()} disabled={uploadSigned.isPending}>
                      <Icon name="upload" /> Unterschriebene Fassung hochladen
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <input ref={fileInput} type="file" hidden accept=".pdf,.docx,.jpg,.jpeg,.png" onChange={(e) => e.target.files?.[0] && (uploadSigned.mutate(e.target.files[0]), (e.target.value = ''))} />
      {rows.some((r) => r.missing_values.length && r.template_key === 'contract_owner' && r.state !== 'present') && (
        <div className="pb" style={{ paddingTop: 0 }}>
          <span className="small miss">Für den Vertrag fehlen: {rows.find((r) => r.template_key === 'contract_owner')!.missing_values.join(', ')}.</span>{' '}
          <button type="button" className="btn link-like small" onClick={() => onOpenItem('besichtigung')}>
            Im Protokoll oder Kontakt ergänzen
          </button>
        </div>
      )}
      {(q.data?.generated.length ?? 0) > 0 && (
        <details className="fs" style={{ margin: bare ? '12px 0 0' : '0 20px 14px' }}>
          <summary>Erzeugte Dateien ({q.data!.generated.length})</summary>
          <ul className="list" style={{ marginTop: 6 }}>
            {q.data!.generated.map((g) => (
              <li key={g.id} style={{ padding: '6px 0' }}>
                <span className="k">{fmtDate(g.created_at)}</span>
                <a className="link" href={`/api/v1/documents/${g.id}/file`} target="_blank" rel="noreferrer">
                  {g.original_name}
                </a>
                {g.created_by_name && <span className="mut small">von {g.created_by_name}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
      {preview && <PreviewModal a={a} templateKey={preview} write={write} onClose={() => setPreview(null)} onGenerate={() => generate.mutate(preview)} busy={generate.isPending} onOpenItem={onOpenItem} />}
    </Wrap>
  );
}

/** Dokumentvorschau (5.2): Vertrag und Pflanzbestätigung mit grün/rot markierten Werten; Protokoll und Monitoring-Doku als Druckansicht */
function PreviewModal({ a, templateKey, write, onClose, onGenerate, busy, onOpenItem }: { a: ActionDetail; templateKey: DocumentTemplateKey; write: boolean; onClose: () => void; onGenerate: () => void; busy: boolean; onOpenItem: (k: string) => void }) {
  const q = useQuery({ queryKey: ['document-preview', a.id, templateKey], queryFn: () => api.get<DocumentPreview>(`/actions/${a.id}/documents/preview?template_key=${templateKey}`) });
  const p = q.data;
  const printUrl = templateKey === 'site_visit' && a.site_visit ? `/api/v1/site-visits/${a.site_visit.id}/print` : templateKey === 'monitoring_doc' && a.area ? `/api/v1/areas/${a.area.id}/monitoring-print` : templateKey === 'planting_confirmation' ? `/api/v1/actions/${a.id}/planting-confirmation-print` : null;
  return (
    <Modal
      title={p?.title ?? 'Vorschau'}
      subtitle={p ? (p.has_file ? `Word-Vorlage Version ${p.version}` : p.engine === 'docx' ? 'Startfassung ohne hochgeladene Word-Vorlage' : 'Druckansicht') : undefined}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Schließen
          </button>
          {printUrl && (
            <a className="btn" href={printUrl} target="_blank" rel="noreferrer">
              <Icon name="external" /> Druckansicht
            </a>
          )}
          {write && p && (
            <button className="btn primary" type="button" onClick={onGenerate} disabled={!p.can_generate || busy}>
              {p.generate_label}
            </button>
          )}
        </>
      }
    >
      {!p ? (
        <Skeleton rows={6} />
      ) : (
        <>
          {p.missing.length > 0 && (
            <Note kind="bad">
              Pflichtwerte fehlen: {p.missing.join(', ')}.{' '}
              <button type="button" className="btn link-like" onClick={() => (onClose(), onOpenItem(templateKey === 'planting_confirmation' ? 'pflanzbestaetigung' : 'besichtigung'))}>
                Im Protokoll ergänzen
              </button>
            </Note>
          )}
          {p.unknown.length > 0 && <Note>Unbekannte Platzhalter in der Word-Vorlage bleiben leer: {p.unknown.join(', ')}</Note>}
          {p.paragraphs.length > 0 ? (
            <div className="doc-preview">
              {p.paragraphs.map((para, i) => (
                <div key={i}>
                  {para.heading && <h4>{para.heading}</h4>}
                  <p className={para.muted ? 'mut' : ''}>
                    {para.parts.map((x, j) =>
                      'text' in x ? (
                        <span key={j}>{x.text}</span>
                      ) : (
                        <span key={j} className={`fill ${x.missing ? 'miss' : ''}`} title={x.key}>
                          {x.missing ? '[fehlt]' : x.value}
                        </span>
                      ),
                    )}
                  </p>
                </div>
              ))}
              <p className="mut small" style={{ marginTop: 16 }}>
                Grün hinterlegte Werte kommen aus Begehungsprotokoll, Aktion und Kontaktdaten. Fehlende Werte bitte im Protokoll oder Kontakt ergänzen.
              </p>
            </div>
          ) : printUrl ? (
            <iframe title="Druckansicht" src={printUrl} style={{ width: '100%', height: '60vh', border: '1px solid var(--line)', borderRadius: 8, background: '#fff' }} />
          ) : (
            <div className="empty">{p.missing.length ? 'Noch nicht verfügbar.' : 'Keine Vorschau.'}</div>
          )}
        </>
      )}
    </Modal>
  );
}
